"use client";

import { Fragment, JSX } from "react";
import { cva } from "styled-system/css";
import { BreakpointToken, token } from "styled-system/tokens";

import { useHydrated } from "@/hooks/use-hydrated";
import { useMediaQuery } from "@/hooks/use-media-query";

export interface MediaQueryProps {
  /** Whether to render the children above or below the provided breakpoint */
  display: "above" | "below";
  /** The breakpoint to determine if the children should be rendered */
  breakpoint: BreakpointToken;
  /**
   * A function that returns the JSX element to render. The function will be passed a className that
   * should be applied to the returned element so that the CSS media query is applied during SSR.
   */
  children: (className: string) => JSX.Element;
}

/**
 * An SSR-safe component for conditionally rendering content based on a CSS media query.
 * When ran on the server, it renders the children with the appropriate CSS media query applied.
 * When ran on the client, it renders the children if the media query matches, otherwise it removes
 * the hidden children from the DOM to reduce overhead.
 */
export function MediaQuery({ display, breakpoint, children }: MediaQueryProps) {
  const isClientSide = useHydrated();

  const bpWidth = token(`breakpoints.${breakpoint}`);
  const query =
    display === "above" ? `(min-width: ${bpWidth})` : `(max-width: calc(${bpWidth} - 1px))`;
  const matches = useMediaQuery(query);

  if (!isClientSide || matches) {
    // Render the children with a CSS media query on the server or if the JS media query matches
    return (
      <Fragment
        key="mounted"
        children={children(
          display === "above"
            ? displayAboveStyles({ breakpoint })
            : displayBelowStyles({ breakpoint }),
        )}
      />
    );
  }

  // Unmount the children on the client-side if the JS media query does not match
  return null;
}

const displayBelowStyles = cva({
  base: {},
  variants: {
    breakpoint: {
      xs: { hideFrom: "xs" },
      sm: { hideFrom: "sm" },
      md: { hideFrom: "md" },
      lg: { hideFrom: "lg" },
      xl: { hideFrom: "xl" },
      xxl: { hideFrom: "xxl" },
      desktop: { hideFrom: "desktop" },
    },
  },
});

const displayAboveStyles = cva({
  base: {},
  variants: {
    breakpoint: {
      xs: { hideBelow: "xs" },
      sm: { hideBelow: "sm" },
      md: { hideBelow: "md" },
      lg: { hideBelow: "lg" },
      xl: { hideBelow: "xl" },
      xxl: { hideBelow: "xxl" },
      desktop: { hideBelow: "desktop" },
    },
  },
});
